import React from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/styles/useTheme';

/**
 * FooterLink renders a single footer navigation link with optional trailing icon.
 *
 * Why this exists:
 * - Keeps footer link structure and accessibility behavior consistent.
 * - Centralizes text/icon sizing so callers pass intent-level props only.
 *
 * Use this when:
 * - Rendering entries from footer link config arrays.
 * - You want one clickable target containing both label and icon.
 *
 * Typical usage:
 * <FooterLink label="privacy_policy" href="..." icon={iconLink} />
 */
function FooterLink({
	href,
	className,
	label,
	icon,
	ariaLabel,
	fontSize,
	iconFontSize,
}) {
	const theme = useTheme();
	const resolvedFontSize =
		typeof fontSize === 'number'
			? `${fontSize}px`
			: fontSize || theme.palette.textSize.sizeSm;
	const resolvedIconFontSize =
		typeof iconFontSize === 'number'
			? `${iconFontSize}px`
			: iconFontSize || theme.palette.textSize.sizeXs;
	const linkProps = href ? { href, rel: 'noreferrer', target: '_blank' } : {};

	return (
		<Link
			underline="hover"
			variant="subtitle2"
			className={className || ''}
			aria-label={ariaLabel || label}
			sx={{
				display: 'inline-flex',
				alignItems: 'baseline',
				gap: '.3rem',
				color: 'inherit',
			}}
			{...linkProps}
		>
			<Stack direction="row" alignItems="baseline" gap={0.7}>
				<Typography sx={{ fontSize: resolvedFontSize }}>{label}</Typography>
				{icon && (
					<Typography
						component="span"
						sx={{
							fontSize: resolvedIconFontSize,
							lineHeight: 1,
							display: 'inline-flex',
							verticalAlign: 'text-bottom',
						}}
						aria-hidden="true"
					>
						{icon}
					</Typography>
				)}
			</Stack>
		</Link>
	);
}

FooterLink.propTypes = {
	href: PropTypes.string,
	className: PropTypes.string,
	label: PropTypes.string.isRequired,
	icon: PropTypes.node,
	ariaLabel: PropTypes.string,
	fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	iconFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FooterLink;
